import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Observable, forkJoin, switchMap } from 'rxjs'

import {
  TrygroupAccessGrantSetting,
  TrygroupDashboardSettingsService,
} from '../../../../services/trygroup-dashboard-settings/trygroup-dashboard-settings.service'
import { UserStore } from '../../../../stores/user.store'

@UntilDestroy()
@Component({
  selector: 'app-trygroup-access-grants-settings-page',
  standalone: true,
  imports: [MatSlideToggle],
  template: `
    <section class="settings-panel">
      <div class="panel-header">
        <div>
          <h3>アクセス権限管理</h3>
          <p>
            トライグループ向けダッシュボードを利用できるユーザーを管理します。
          </p>
        </div>
      </div>

      @if (isLoading) {
        <p class="status">読み込み中です。</p>
      } @else if (errorMessage) {
        <p class="error">{{ errorMessage }}</p>
      } @else if (accessGrantSettings.length === 0) {
        <p class="status">ユーザーがありません。</p>
      } @else {
        <table class="access-grants-table">
          <thead>
            <tr>
              <th>ユーザー</th>
              <th>部署</th>
              <th>ロール</th>
              <th>状態</th>
              <th>権限</th>
            </tr>
          </thead>
          <tbody>
            @for (setting of accessGrantSettings; track setting.user.id) {
              <tr>
                <td>
                  <span class="user-name">{{ setting.user.name }}</span>
                  <span class="user-email">{{ setting.user.email }}</span>
                </td>
                <td>{{ formatDepartmentName(setting.user.departmentName) }}</td>
                <td>{{ formatRoles(setting) }}</td>
                <td>
                  <span
                    class="status-badge"
                    [class.status-badge-enabled]="isAutoAllowed(setting)"
                    [class.status-badge-granted]="
                      !isAutoAllowed(setting) && setting.granted
                    "
                  >
                    {{ formatGrantStatus(setting) }}
                  </span>
                </td>
                <td>
                  <mat-slide-toggle
                    color="primary"
                    [checked]="setting.granted || isAutoAllowed(setting)"
                    [disabled]="isToggleDisabled(setting)"
                    [attr.aria-label]="setting.user.name + ' のアクセス権限'"
                    (change)="onToggleChange(setting, $event)"
                  ></mat-slide-toggle>
                </td>
              </tr>
            }
          </tbody>
        </table>
      }

      @if (savedMessage) {
        <p class="saved">{{ savedMessage }}</p>
      }
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .settings-panel {
        padding: 20px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgb(15 23 42 / 6%);
      }

      .panel-header {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 16px;
      }

      h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
      }

      p {
        margin: 6px 0 0;
        color: #64748b;
        font-size: 13px;
      }

      .access-grants-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
      }

      th,
      td {
        padding: 12px 10px;
        border-bottom: 1px solid #e2e8f0;
        text-align: left;
        vertical-align: middle;
      }

      th {
        color: #475569;
        font-weight: 800;
      }

      .user-name,
      .user-email {
        display: block;
      }

      .user-name {
        color: #0f172a;
        font-weight: 700;
      }

      .user-email {
        margin-top: 4px;
        color: #64748b;
      }

      .status-badge {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 2px 8px;
        color: #475569;
        font-weight: 700;
        background: #f8fafc;
        border: 1px solid #cbd5e1;
        border-radius: 999px;
      }

      .status-badge-granted {
        color: #047857;
        background: #ecfdf5;
        border-color: #a7f3d0;
      }

      .status-badge-enabled {
        color: #1d4ed8;
        background: #eff6ff;
        border-color: #bfdbfe;
      }

      .saved {
        color: #059669;
        font-weight: 700;
      }

      .status {
        color: #64748b;
      }

      .error {
        color: #dc2626;
        font-weight: 700;
      }
    `,
  ],
})
export class TrygroupAccessGrantsSettingsPageComponent implements OnInit {
  accessGrantSettings: TrygroupAccessGrantSetting[] = []
  pendingUserIds = new Set<string>()
  currentUserId = ''
  isLoading = true
  errorMessage = ''
  savedMessage = ''

  constructor(
    private trygroupDashboardSettingsService: TrygroupDashboardSettingsService,
    private userStore: UserStore,
  ) {}

  ngOnInit(): void {
    this.load()
  }

  onToggleChange(
    setting: TrygroupAccessGrantSetting,
    event: MatSlideToggleChange,
  ): void {
    if (this.isToggleDisabled(setting)) {
      event.source.checked = setting.granted || this.isAutoAllowed(setting)
      return
    }

    const userId = setting.user.id
    this.setPending(userId, true)
    this.savedMessage = ''
    this.errorMessage = ''

    const mutation$: Observable<unknown> = event.checked
      ? this.trygroupDashboardSettingsService.grantTrygroupDashboardAccess(
          userId,
        )
      : this.trygroupDashboardSettingsService.revokeTrygroupDashboardAccess(
          userId,
        )

    mutation$
      .pipe(
        switchMap(() =>
          this.trygroupDashboardSettingsService.getAccessGrantSettings(),
        ),
        untilDestroyed(this),
      )
      .subscribe({
        next: (settings) => {
          this.accessGrantSettings = settings
          this.setPending(userId, false)
          this.savedMessage = event.checked
            ? 'アクセス権限を付与しました。'
            : 'アクセス権限を解除しました。'
        },
        error: () => {
          event.source.checked = setting.granted || this.isAutoAllowed(setting)
          this.setPending(userId, false)
          this.errorMessage = 'アクセス権限の更新に失敗しました。'
        },
      })
  }

  isToggleDisabled(setting: TrygroupAccessGrantSetting): boolean {
    return (
      this.pendingUserIds.has(setting.user.id) ||
      setting.user.id === this.currentUserId ||
      this.isAutoAllowed(setting)
    )
  }

  isAutoAllowed(setting: TrygroupAccessGrantSetting): boolean {
    return setting.isSuperAdmin
  }

  formatDepartmentName(departmentName: string | null): string {
    if (!departmentName || departmentName === 'noDepartment') {
      return '所属無し'
    }

    return departmentName
  }

  formatRoles(setting: TrygroupAccessGrantSetting): string {
    if (setting.user.roles.length === 0) {
      return '-'
    }

    return setting.user.roles.map((role) => role.name).join(', ')
  }

  formatGrantStatus(setting: TrygroupAccessGrantSetting): string {
    if (this.isAutoAllowed(setting)) {
      return '自動許可'
    }

    return setting.granted ? '付与済み' : '未付与'
  }

  private load(): void {
    this.isLoading = true
    forkJoin({
      currentUser: this.userStore.getOneSelf({ useCache: true }),
      settings: this.trygroupDashboardSettingsService.getAccessGrantSettings(),
    })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ({ currentUser, settings }) => {
          this.currentUserId = currentUser.id
          this.accessGrantSettings = settings
          this.isLoading = false
          this.errorMessage = ''
        },
        error: () => {
          this.isLoading = false
          this.errorMessage = 'アクセス権限設定の取得に失敗しました。'
        },
      })
  }

  private setPending(userId: string, pending: boolean): void {
    const nextPendingUserIds = new Set(this.pendingUserIds)
    if (pending) {
      nextPendingUserIds.add(userId)
    } else {
      nextPendingUserIds.delete(userId)
    }
    this.pendingUserIds = nextPendingUserIds
  }
}
